import { AddressId } from '../domain/entities/Address';
import ShippingRepository from '../repositories/ShippingRepository';
import { diContainer, serviceNames } from '../diContainer/diContainer';
import BillingRepository from '../repositories/BillingRepository';
import ShippingUIState from '../stores/UIState/ShippingUIState';
import Config from '../domain/entities/Config';
import TransactionLoadHandler, { ITransactionLoad } from './TransactionLoadHandler';
import { FIELD_TYPE } from '../constants/Constants';
import NotificationsCollection from '../domain/entities/Notification/NotificationsCollection';
import { RESTRICTED_ADDRESS_ERRORS, PRO } from '../constants/Errors';
import { computed } from 'mobx';

class SelectShippingAddressUseCase extends TransactionLoadHandler {
  private id: AddressId;

  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private isBillingSameAsShipping: boolean;

  private shouldSaveAddress: boolean;

  private shippingUIState: ShippingUIState;

  private config: Config;

  private notificationsCollection: NotificationsCollection;

  private requestAbortController: AbortController | null = null;

  private static activeAbortController: AbortController | null = null;

  private retriedAddressSelection = false;

  constructor(id: AddressId, isBillingSameAsShipping: boolean, shouldSaveAddress = true, options?: ITransactionLoad) {
    super({ ...options?.transactionLoadOptions, isControlled: true });
    this.id = id;
    this.isBillingSameAsShipping = isBillingSameAsShipping;
    this.shouldSaveAddress = shouldSaveAddress;
    this.shippingRepository = diContainer.get(serviceNames.shippingRepository);
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.shippingUIState = diContainer.get(serviceNames.shippingUIState);
    this.config = diContainer.get(serviceNames.config);
    this.notificationsCollection = diContainer.get(serviceNames.notificationsCollection);
  }

  /**
   * Checks if this execution should be cancelled due to a newer request
   */
  @computed
  private get shouldCancelCurrentExecution(): boolean {
    return this.currentRequestId !== SelectShippingAddressUseCase.lastRequestId;
  }

  /**
   * Cancels the current request by aborting its signal
   */
  private cancelActiveRequest = (reason: string): boolean => {
    const { activeAbortController } = SelectShippingAddressUseCase;

    if (activeAbortController && !activeAbortController.signal.aborted) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      activeAbortController.abort(reason);
      return true;
    }
    return false;
  };

  private reExecuteOnInvalidAddressSelection = async (): Promise<void> => {
    const selectedAddress = this.transactionRepository.transaction?.shippingAddress;

    if (!this.retriedAddressSelection && selectedAddress?.id !== this.id) {
      // Re-run execution once again since the backend might incorrectly return the last address selection due to concurrency issues.
      // This is to ensure the final selected address is correct and notifications are properly cleared.
      this.notificationsCollection.clearNotificationByKeys(RESTRICTED_ADDRESS_ERRORS);
      this.retriedAddressSelection = true;
      await this.run();
    }
  };

  public run = async (): Promise<void> => {
    // Cancel any previous in-flight request immediately before starting a new one.
    const bypassActiveRequest = this.cancelActiveRequest('New address selection initiated, cancelling previous request');
    SelectShippingAddressUseCase.lastRequestId = this.currentRequestId;

    // Initialize abort controller for this request
    this.requestAbortController = new AbortController();
    SelectShippingAddressUseCase.activeAbortController = this.requestAbortController;
    const { signal } = this.requestAbortController;

    try {
      const transaction = await this.transactionRepository.getTransaction(bypassActiveRequest);

      const selectedAddress = this.shippingRepository.getAddressById(this.id);
      this.shippingUIState.updateContinueLoadingButtonState(true);
      this.shippingUIState.updateAddressSelectionLoadingState(true);

      // reload shipping methods if new selected country is different from previous saved transaction countryId
      // and multiple countries can be used
      const transactionAddressCountryId = transaction?.shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
      const formStoreCountryId = this.shippingRepository.formStore.values[FIELD_TYPE.COUNTRY_ID];
      const previousCountrySelection = transactionAddressCountryId || formStoreCountryId;
      const shouldLoadShippingMethods = (this.config.shippingCountries?.length > 1 &&
        selectedAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID] !== previousCountrySelection) || this.config.useShippingCalculator;

      transaction.setShippingAddress(selectedAddress);

      if (shouldLoadShippingMethods) {
        const countryId = selectedAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
        if (countryId !== undefined) {
          await this.transactionRepository.loadShippingMethods(countryId as number, {
            debounceRequest: false,
            reloadTransaction: false,
            signal,
          });
        }
      }

      if (this.isBillingSameAsShipping) {
        transaction.setBillingAddress(selectedAddress);
      }

      if (!this.shouldSaveAddress) {
        if (this.shippingRepository.config?.useShippingCalculator) {
          // Flow: User is canceling the new address he was trying to add, but order summary might have changed during shippingCalc api call.
          // We need to refresh the order summary using current (previous) address selection, hence the call below.
          this.transactionRepository.loadTransaction(undefined, signal);
        }
        this.shippingUIState.updateContinueLoadingButtonState(false);
        this.shippingUIState.updateAddressSelectionLoadingState(false);
        return;
      }

      await this.shippingRepository.onSelectAddress(this.id, { signal });

      if (this.isBillingSameAsShipping) {
        await this.billingRepository.selectAddress(this.id, { signal });
      }

      // clear restricted address notifications even after success, to overcome race conditions issues, where we allow async address selections
      this.notificationsCollection.clearNotificationByKeys(RESTRICTED_ADDRESS_ERRORS);
      this.notificationsCollection.clearNotificationByKey(PRO.BILLSHIP_MISMATCH);

      await this.handleTransactionUpdate(signal);
      await this.reExecuteOnInvalidAddressSelection();
    } catch (error) {
      if (signal.aborted) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        console.log(`Fetch aborted: ${this.requestAbortController.signal.reason}`);
      }
    } finally {
      if (SelectShippingAddressUseCase.activeAbortController === this.requestAbortController) {
        SelectShippingAddressUseCase.activeAbortController = null;
      }
      if (!this.shouldCancelCurrentExecution && !signal.aborted) {
        this.shippingUIState.updateContinueLoadingButtonState(false);
        this.shippingUIState.updateAddressSelectionLoadingState(false);
      }
    }
  };
}

export default SelectShippingAddressUseCase;
